import React from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  Grid,
  Box,
  Typography,
  Card,
  CardContent,
  Skeleton,
  Switch,
  FormControlLabel,
  FormGroup,
  Checkbox,
  CardHeader,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import FormHelperText from '@mui/material/FormHelperText';
import makeStyles from '@mui/styles/makeStyles';
import { TwitterPicker } from 'react-color';

import {
  AccessTime,
  Email,
  Notifications,
  PhoneIphone,
  Visibility,
} from '@mui/icons-material';
import { primary } from '../../../../../../../utils/brandColors';
import colors from '../../../../../../../utils/colors';
import styles from './styles';
import HorseBillablePrice from '../../../../../../../components/EventData/HorseBillablePrice';
import HorseNote from '../../../../../../../components/EventData/HorseNote';

const useStyles = makeStyles(styles);
const allNotificationChannels = ['app', 'mobile', 'email'];

export const DataFormComponent = (props) => {
  const classes = useStyles();
  const {
    eventTemplate,
    barn,
    handleChangeEventDataDefaultValues,
    isLoading,
    isHorseTask,
    handleChangeNotificationChannels,
    notificationChannels,
    eventData,
  } = props;

  // Find the horse or barn task id in event template tags, set value if present
  let taskTypeValue = eventTemplate.tags && eventTemplate.tags.length === 1
    ? parseInt(eventTemplate.tags[0], 10)
    : null;

  if (!taskTypeValue && barn.barnMetadata.horseTaskTypeTagId
    && eventTemplate.tags.indexOf(barn.barnMetadata.horseTaskTypeTagId) >= 0) {
    taskTypeValue = barn.barnMetadata.horseTaskTypeTagId;
  } else if (!taskTypeValue && barn.barnMetadata.barnTaskTypeTagId
    && eventTemplate.tags.indexOf(barn.barnMetadata.barnTaskTypeTagId) >= 0) {
    taskTypeValue = barn.barnMetadata.barnTaskTypeTagId;
  } else if (!taskTypeValue && barn.barnMetadata.administrativeTaskTypeTagId
    && eventTemplate.tags.indexOf(barn.barnMetadata.administrativeTaskTypeTagId) >= 0) {
    taskTypeValue = barn.barnMetadata.administrativeTaskTypeTagId;
  }

  const handleChangeNotificationChannel = (event) => {
    let updatedNotificationChannels = notificationChannels || [];
    if (notificationChannels.indexOf(event.target.name) >= 0) {
      updatedNotificationChannels = notificationChannels.filter((c) => c !== event.target.name);
    } else if (!notificationChannels || notificationChannels.length === 0) {
      // empty list so we actually want to add the other two options to the array and not add this one
      updatedNotificationChannels = allNotificationChannels.filter((c) => c !== event.target.name)
    } else {
      updatedNotificationChannels = [...notificationChannels, event.target.name]
    }
    handleChangeNotificationChannels(updatedNotificationChannels)
  };
  const getEDNameFromId = (id) => (eventData.find((ed) => ed.id === id) || { name: 'Unknown' }).name

  const label = (
    <Grid item xs={12} lg={2}>
      <Typography variant="subtitle2">Display &amp; Task Behavior</Typography>
      <Typography variant="caption" gutterBottom>
        Define display settings and the
        behavior tasks should take.
      </Typography>
    </Grid>
  );

  const labelAfterCompletion = (
    <Grid item xs={12} lg={2}>
      <Typography variant="subtitle2">After Task Completion Actions</Typography>
      <Typography variant="caption" gutterBottom>
        Define actions taken after a task is completed.
      </Typography>
    </Grid>
  );

  if (isLoading) {
    return (
      <Grid container spacing={4}>
        {label}
        <Grid item xs={12} lg={10} container className={classes.section}>
          <Card className={classes.card}>
            <CardContent>
              <Skeleton className={classes.nameField} />
              <Skeleton />
              <Skeleton />
              <Skeleton />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={4}>
      {label}
      <Grid item xs={12} lg={10} container className={classes.section}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Card className={classes.card}>
              <CardHeader
                sx={{ pb: 0 }}
                title={(
                  <Box sx={{ display: 'flex' }}>
                    <Box>Task Display Color</Box>
                    <div
                      className="circle"
                      style={{
                        marginLeft: '0.5rem',
                        height: '25px',
                        width: '25px',
                        borderRadius: '25px',
                        backgroundColor:
                    (eventTemplate.eventDataDefaultValues
                        && eventTemplate.eventDataDefaultValues[getEDNameFromId(barn.barnMetadata.calendarDisplayColorEventDataId)]) || primary.main,
                      }}
                      role="presentation"
                    />
                  </Box>
                )}
              />
              <CardContent sx={{ display: 'flex' }}>
                <FormControl
                  margin="normal"
                  fullWidth
                  variant="outlined"
                >
                  <TwitterPicker
                    colors={colors}
                    color={(eventTemplate.eventDataDefaultValues && eventTemplate.eventDataDefaultValues.calendarDisplayColor) || primary.main}
                    onChange={(e) => {
                      const calendarDisplayColor = e.hex;
                      handleChangeEventDataDefaultValues({
                        ...eventTemplate.eventDataDefaultValues,
                        [getEDNameFromId(barn.barnMetadata.calendarDisplayColorEventDataId)]: calendarDisplayColor,
                      });
                    }}
                    direction="vertical"
                  />
                  <Box
                    sx={{
                      mx: 2,
                      pb: 1,
                      fontSize: 14,
                      fontWeight: 300,
                      zIndex: 10000,
                      position: 'relative',
                    }}
                  >
                    <Typography fontWeight={300} component="div">Pick your own color on the</Typography>
                    <a href="https://g.co/kgs/rxPsQ5R" target="blank">Google Hex Color Picker</a>
                  </Box>
                </FormControl>
              </CardContent>
            </Card>
          </Grid>
          {
            isHorseTask && (
              <Grid item xs={12} md={4}>
                <Card className={classes.card}>
                  <CardHeader
                    sx={{ pb: 0 }}
                    title={(
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mr: 1,
                          }}
                        ><AccessTime color="primary" sx={{ mr: 1 }} />Default Duration
                        </Box>
                        <FormControl>
                          <InputLabel id="demo-simple-select-label">Duration</InputLabel>
                          <Select
                            size="small"
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            value={(eventTemplate.eventDataDefaultValues && eventTemplate.eventDataDefaultValues.defaultDuration
                               && parseInt(eventTemplate.eventDataDefaultValues.defaultDuration, 10)) || 1800000}
                            label="Duration"
                            onChange={(e) => {
                              handleChangeEventDataDefaultValues({
                                ...eventTemplate.eventDataDefaultValues,
                                defaultDuration: e.target.value,
                              });
                            }}
                          >
                            <MenuItem value="900000">15 minutes</MenuItem>
                            <MenuItem value="1800000">30 minutes</MenuItem>
                            <MenuItem value="2700000">45 minutes</MenuItem>
                            <MenuItem value="3600000">1 hour</MenuItem>
                            <MenuItem value="4500000">1 hour 15 minutes</MenuItem>
                            <MenuItem value="5400000">1 hour 30 minutes</MenuItem>
                            <MenuItem value="6300000">1 hour 45 minutes</MenuItem>
                            <MenuItem value="7200000">2 hours</MenuItem>
                            <MenuItem value="9000000">2 hours 30 minutes</MenuItem>
                            <MenuItem value="10800000">3 hours</MenuItem>
                            <MenuItem value="14400000">4 hours</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    )}
                    subheader="Set the default duration for this task when specifying a time during task scheduling."
                  />
                  <CardHeader
                    sx={{ pb: 0 }}
                    title={(
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        ><Visibility color="primary" sx={{ mr: 1 }} />Client Viewable
                        </Box>

                        <Switch
                          checked={eventTemplate.eventDataDefaultValues && eventTemplate.eventDataDefaultValues.clientViewable === 'Yes'}
                          onChange={(e) => {
                            handleChangeEventDataDefaultValues({
                              ...eventTemplate.eventDataDefaultValues,
                              [getEDNameFromId(barn.barnMetadata.clientViewableEventDataId)]: e.target.checked
                                ? 'Yes'
                                : 'No',
                            });
                          }}
                          inputProps={{ 'aria-label': 'controlled' }}
                        />
                      </Box>
                    )}
                    subheader="Allow clients to view this task. They will not be able to interact with the task unless they are assigned to it."
                  />
                </Card>
              </Grid>
            )
          }

          <Grid item xs={12} md={4}>
            <Card className={classes.card}>
              <CardHeader
                sx={{ pb: 0 }}
                title="Task Notifications"
              />
              <CardContent>
                <FormControl
                  margin="normal"
                  fullWidth
                >
                  <FormGroup component="fieldset" variant="standard">
                    <FormControlLabel
                      control={<Checkbox name="email" checked={!eventTemplate.notificationChannels || eventTemplate.notificationChannels.length === 0 || eventTemplate.notificationChannels.indexOf('email') >= 0} onChange={handleChangeNotificationChannel} />}
                      label={(
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <Email color="secondary" sx={{ mr: 1 }} /> Email
                        </Box>
                      )}
                    />
                    <FormControlLabel
                      control={<Checkbox name="mobile" checked={!eventTemplate.notificationChannels || eventTemplate.notificationChannels.length === 0 || eventTemplate.notificationChannels.indexOf('mobile') >= 0} onChange={handleChangeNotificationChannel} />}
                      label={(
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <PhoneIphone color="secondary" sx={{ mr: 1 }} /> Mobile (app)
                        </Box>
                      )}
                    />
                    <FormControlLabel
                      control={<Checkbox name="app" checked={!eventTemplate.notificationChannels || eventTemplate.notificationChannels.length === 0 || eventTemplate.notificationChannels.indexOf('app') >= 0} onChange={handleChangeNotificationChannel} />}
                      label={(
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <Notifications color="secondary" sx={{ mr: 1 }} /> In-App (website)
                        </Box>
                      )}
                    />
                  </FormGroup>
                  <FormHelperText>
                    Select the notification channels used to notify assigned users about this task.
                  </FormHelperText>
                </FormControl>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>

      { isHorseTask && (labelAfterCompletion) }
      {
      isHorseTask && (
        <Grid item xs={12} md={9} lg={10} container className={classes.section}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card className={classes.card} sx={{ pb: 2 }}>
                <HorseBillablePrice
                  onChange={handleChangeEventDataDefaultValues}
                  eventDataDefaultValues={eventTemplate.eventDataDefaultValues}
                  showHeader
                  showDescription
                />
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card className={classes.card} sx={{ pb: 2 }}>
                <HorseNote
                  onChange={handleChangeEventDataDefaultValues}
                  eventDataDefaultValues={eventTemplate.eventDataDefaultValues}
                  showHeader
                  showDescription
                />
              </Card>
            </Grid>
          </Grid>
        </Grid>
      )
    }
    </Grid>
  );
};

DataFormComponent.propTypes = {
  eventTemplate: PropTypes.object,
  barn: PropTypes.object,
  tags: PropTypes.array,
  eventData: PropTypes.array,
  handleChangeEventDataDefaultValues: PropTypes.func,
  isLoading: PropTypes.bool,
  isHorseTask: PropTypes.bool,
  handleChangeNotificationChannels: PropTypes.func,
  notificationChannels: PropTypes.array,
};

DataFormComponent.defaultProps = {
  eventTemplate: {},
  barn: { barnMetadata: {} },
  tags: [],
  eventData: [],
  handleChangeEventDataDefaultValues: () => {},
  isLoading: true,
  isHorseTask: false,
  handleChangeNotificationChannels: () => {},
  notificationChannels: [],
};

export default DataFormComponent;
